import { useState } from "react";
import { useDispatch } from "react-redux";
import { setGoingToClose } from "../../redux/rightModalSlice";
import DynamicIcon from "../DynamicIcon";
import Loader from "../Loader";

export default function AgentCredentials({ agentId, agentName, agentKey: initialAgentKey }) {
    const dispatch = useDispatch();
    const token = localStorage.getItem('installamelo-auth-token') ?? '';
    const storedUser = JSON.parse(localStorage.getItem('installamelo-auth-user') || '{}');
    const accountId = storedUser.accountId || '';

    const [copiedField, setCopiedField] = useState(null);
    const [regenStatus, setRegenStatus] = useState('i');
    const [regenError, setRegenError] = useState('');
    const [credentials, setCredentials] = useState(null);

    const copyToClipboard = (text, field) => {
        navigator.clipboard.writeText(text);
        setCopiedField(field);
        setTimeout(() => setCopiedField(null), 2000);
    };

    const handleRegenerateSecret = async () => {
        setRegenStatus('l');
        setRegenError('');

        try {
            const url = `${import.meta.env.VITE_API_BASE_URL}/agents/${agentId}/regenerate-secret`;
            const response = await fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'auth-token': token }
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || "Errore nella rigenerazione del secret");
            }

            const result = await response.json();
            setCredentials({
                agentKey: result.agentKey,
                agentSecret: result.agentSecret
            });
            setRegenStatus('s');
        } catch (error) {
            setRegenError(error.message);
            setRegenStatus('f');
        }
    };

    return (
        <div className="w-full p-8">
            <h1 className="flex items-center gap-2 mb-6">
                <DynamicIcon iconClass="fa-solid fa-key" />
                Credenziali: {agentName}
            </h1>

            {/* Agent Key */}
            <div className="flex flex-col gap-4 border-2 rounded-lg p-4 border-blue-200">
                <div className="input-box">
                    <label className="font-medium">Agent Key</label>
                    <div className="flex gap-2 items-center">
                        <input
                            type="text"
                            value={credentials?.agentKey || initialAgentKey || ''}
                            readOnly
                            className="flex-1 font-mono text-sm"
                        />
                        <button
                            className="px-3 py-1 bg-blue-500 text-white rounded text-sm hover:bg-blue-600 whitespace-nowrap"
                            onClick={() => copyToClipboard(credentials?.agentKey || initialAgentKey, 'key')}
                        >
                            {copiedField === 'key' ? 'Copiato!' : 'Copia'}
                        </button>
                    </div>
                </div>

                <div className="input-box">
                    <label className="font-medium">Account ID</label>
                    <div className="flex gap-2 items-center">
                        <input
                            type="text"
                            value={accountId}
                            readOnly
                            className="flex-1 font-mono text-sm"
                        />
                        <button
                            className="px-3 py-1 bg-blue-500 text-white rounded text-sm hover:bg-blue-600 whitespace-nowrap"
                            onClick={() => copyToClipboard(accountId, 'accountId')}
                        >
                            {copiedField === 'accountId' ? 'Copiato!' : 'Copia'}
                        </button>
                    </div>
                </div>
            </div>

            {/* Secret: rigenerato */}
            {credentials?.agentSecret && (
                <div className="flex flex-col gap-4 mt-4 border-2 rounded-lg p-4 border-green-300 bg-green-50">
                    <div className="bg-yellow-100 border border-yellow-400 text-yellow-800 px-4 py-3 rounded text-sm">
                        <DynamicIcon iconClass="fa-solid fa-triangle-exclamation" /> Copia e conserva il secret adesso. Non sara' piu' visibile dopo la chiusura.
                    </div>
                    <div className="input-box">
                        <label className="font-medium">Agent Secret</label>
                        <div className="flex gap-2 items-center">
                            <input
                                type="text"
                                value={credentials.agentSecret}
                                readOnly
                                className="flex-1 font-mono text-sm"
                            />
                            <button
                                className="px-3 py-1 bg-blue-500 text-white rounded text-sm hover:bg-blue-600 whitespace-nowrap"
                                onClick={() => copyToClipboard(credentials.agentSecret, 'secret')}
                            >
                                {copiedField === 'secret' ? 'Copiato!' : 'Copia'}
                            </button>
                        </div>
                    </div>

                    {/* Esempio curl */}
                    <div>
                        <label className="font-medium text-sm">Esempio d'uso (curl)</label>
                        <div className="relative">
                            <pre className="bg-gray-900 text-green-400 p-3 rounded text-xs overflow-x-auto whitespace-pre">{`curl -X POST \\
     -H "x-agent-key: ${credentials.agentKey}" \\
     -H "x-agent-secret: ${credentials.agentSecret}" \\
     -H "x-account-id: ${accountId}" \\
     -H "Content-Type: application/json" \\
     -d '{}' \\
     ${import.meta.env.VITE_API_BASE_URL}/ai/query/customers`}</pre>
                            <button
                                className="absolute top-2 right-2 px-2 py-1 bg-gray-700 text-white rounded text-xs hover:bg-gray-600"
                                onClick={() => copyToClipboard(
                                    `curl -X POST -H "x-agent-key: ${credentials.agentKey}" -H "x-agent-secret: ${credentials.agentSecret}" -H "x-account-id: ${accountId}" -H "Content-Type: application/json" -d '{}' ${import.meta.env.VITE_API_BASE_URL}/ai/query/customers`,
                                    'curl'
                                )}
                            >
                                {copiedField === 'curl' ? 'Copiato!' : 'Copia'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Rigenera Secret */}
            <div className="mt-4 border-2 rounded-lg p-4 border-orange-200">
                <h2 className="mb-2 text-sm font-medium">Agent Secret</h2>
                <p className="text-sm text-gray-500 mb-3">
                    Il secret e' criptato e non recuperabile. Per ottenere un nuovo secret, rigeneralo.
                    Il vecchio secret verra' invalidato.
                </p>
                <div className="flex items-center gap-3">
                    <button
                        className="px-4 py-2 bg-orange-500 text-white rounded hover:bg-orange-600 transition-colors flex items-center gap-2 text-sm"
                        onClick={handleRegenerateSecret}
                        disabled={regenStatus === 'l'}
                    >
                        <DynamicIcon iconClass="fa-solid fa-rotate" />
                        Rigenera Secret
                    </button>
                    {regenStatus === 'l' && <Loader />}
                    {regenStatus === 'f' && <span className="text-red-600 text-sm">{regenError}</span>}
                </div>
            </div>

            {/* Chiudi */}
            <div className="mt-6">
                <button className="secondary" onClick={() => dispatch(setGoingToClose())}>
                    Chiudi
                </button>
            </div>
        </div>
    );
}
